import React, { useEffect, useMemo, useState } from 'react';
import {
  getCompactPlanCardFeatures,
  getRegionFromCountryCode,
  normalizeClubPlanTier,
  resolveSubscriptionPlanPrice,
  resolveSubscriptionTierForDisplay,
  type Plan,
  type PlanRecommendationResult,
  type SubscriptionTier,
} from '@court-sync/shared';
import {
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Crown,
  Loader2,
  ShieldCheck,
  Star,
  Zap,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { subscriptionService } from '@/services/subscription';
import regionService from '@/services/regionService';
import { formatCurrency, getCurrencyFromRegion, getCurrencySymbol, type Currency } from '@/utils/currency';
import {
  persistBillingInterval,
  readStoredBillingInterval,
  resolveInitialBillingInterval,
} from '@/utils/billingInterval';
import { getStarterLimitationSummary } from '@/utils/planRecommendationUi';
import { PlanRecommendationBanner } from './PlanRecommendationBanner';
import { StarterLimitationsModal } from './StarterLimitationsModal';

const PLAN_SORT_ORDER = ['Starter', 'Professional', 'Enterprise'] as const;
const COMPACT_FEATURE_PREVIEW_COUNT = 4;

function getPlanIcon(tier: SubscriptionTier) {
  if (tier === 'starter') return <Star className="h-6 w-6" />;
  if (tier === 'professional') return <Zap className="h-6 w-6" />;
  if (tier === 'enterprise') return <ShieldCheck className="h-6 w-6" />;
  return <Crown className="h-6 w-6" />;
}

function sortPlans(plans: Plan[]): Plan[] {
  return [...plans].sort((a, b) => {
    const indexA = PLAN_SORT_ORDER.findIndex((name) => a.name.includes(name));
    const indexB = PLAN_SORT_ORDER.findIndex((name) => b.name.includes(name));
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });
}

export interface OnboardingPlanSelectionProps {
  recommendation: PlanRecommendationResult;
  selectedPlanTier: SubscriptionTier;
  onSelectPlanTier?: (tier: SubscriptionTier, options?: { starterAcknowledged?: boolean }) => void;
  marketCountry?: string | null;
}

export function OnboardingPlanSelection({
  recommendation,
  selectedPlanTier,
  onSelectPlanTier,
  marketCountry,
}: OnboardingPlanSelectionProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(() =>
    resolveInitialBillingInterval(null, readStoredBillingInterval())
  );
  const [starterModalOpen, setStarterModalOpen] = useState(false);
  const [expandedPlanTiers, setExpandedPlanTiers] = useState<Set<SubscriptionTier>>(new Set());

  const limitations = getStarterLimitationSummary(recommendation);
  const recommendedTier = recommendation.recommendedTier;

  const currency = useMemo<Currency>(() => {
    const region = getRegionFromCountryCode(marketCountry);
    return getCurrencyFromRegion(region);
  }, [marketCountry]);

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      try {
        setLoading(true);
        setError(null);

        let region = getRegionFromCountryCode(marketCountry);
        if (!marketCountry) {
          try {
            region = await regionService.detectRegion();
          } catch {
            // keep country-derived or default region
          }
        }

        const fetchedPlans = await subscriptionService.getPlans(region, 'academy');
        const academyPlans = fetchedPlans.filter(
          (plan) => !normalizeClubPlanTier(plan.internalId ?? null),
        );
        if (!cancelled) {
          setPlans(sortPlans(academyPlans));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load plans');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadPlans();
    return () => {
      cancelled = true;
    };
  }, [marketCountry]);

  const handleBillingIntervalChange = (interval: 'month' | 'year') => {
    setBillingInterval(interval);
    persistBillingInterval(interval);
  };

  const getPlanPrice = (plan: Plan, interval: 'month' | 'year' = billingInterval): number => {
    const resolved = resolveSubscriptionPlanPrice(plan, {
      currency,
      interval,
    });
    return resolved?.amountMajor ?? 0;
  };

  const formatPlanPrice = (plan: Plan, interval: 'month' | 'year' = billingInterval): string => {
    return formatCurrency(getPlanPrice(plan, interval), currency);
  };

  const formatRoundedMonthlyFromYearly = (plan: Plan): string => {
    const yearlyPrice = getPlanPrice(plan, 'year');
    return `${getCurrencySymbol(currency)}${Math.round(yearlyPrice / 12)}`;
  };

  const handlePlanSelect = (tier: SubscriptionTier) => {
    if (tier === 'starter' && recommendedTier !== 'starter') {
      setStarterModalOpen(true);
      return;
    }
    onSelectPlanTier?.(tier);
  };

  if (loading) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden />
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading plans…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PlanRecommendationBanner
        recommendation={recommendation}
        selectedTier={selectedPlanTier}
      />

      <div className="flex justify-center sm:justify-start">
        <div
          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50/90 p-1 shadow-sm dark:border-slate-600 dark:bg-slate-900/50"
          role="group"
          aria-label="Billing period"
        >
          <button
            type="button"
            onClick={() => handleBillingIntervalChange('month')}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
              billingInterval === 'month'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => handleBillingIntervalChange('year')}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
              billingInterval === 'year'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            }`}
          >
            Yearly
            <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {plans.map((plan) => {
          const tier = resolveSubscriptionTierForDisplay(plan.name, plan.internalId ?? null);
          const isRecommendedPlan = tier === recommendedTier;
          const isSelected = selectedPlanTier === tier;
          const allFeatures = getCompactPlanCardFeatures({
            name: plan.name,
            internalId: plan.internalId ?? null,
          });
          const isFeaturesExpanded = expandedPlanTiers.has(tier);
          const visibleFeatures = isFeaturesExpanded
            ? allFeatures
            : allFeatures.slice(0, COMPACT_FEATURE_PREVIEW_COUNT);
          const hasMoreFeatures = allFeatures.length > COMPACT_FEATURE_PREVIEW_COUNT;

          return (
            <Card
              key={plan.id}
              className={`relative overflow-visible border-slate-200/90 transition-shadow duration-200 hover:shadow-md dark:border-slate-700 ${
                isSelected || isRecommendedPlan
                  ? 'border-primary-300 shadow-md ring-2 ring-primary-500/25 dark:border-primary-600 dark:ring-primary-400/20'
                  : ''
              }`}
            >
              {isRecommendedPlan ? (
                <div className="pointer-events-none absolute -top-3 left-4 z-10">
                  <span className="whitespace-nowrap rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-white shadow-md">
                    Recommended
                  </span>
                </div>
              ) : null}

              <div className="flex w-full flex-col p-5 pt-7 sm:flex-row sm:items-start sm:gap-5">
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    className="flex w-full items-start gap-4 text-left"
                    onClick={() => handlePlanSelect(tier)}
                    aria-pressed={isSelected}
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/60 dark:text-primary-400">
                      {getPlanIcon(tier)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">{plan.name}</h3>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        {plan.description || 'Perfect for getting started'}
                      </p>
                    </div>
                  </button>
                  <ul
                    className="mt-3 space-y-1 ml-16"
                    aria-label={`${plan.name} features`}
                  >
                    {visibleFeatures.map((feature) => (
                      <li
                        key={feature}
                        className="flex gap-2 text-xs leading-snug text-slate-600 dark:text-slate-300"
                      >
                        <CheckCircle className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {hasMoreFeatures ? (
                    <button
                      type="button"
                      className="mt-2 ml-16 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      aria-expanded={isFeaturesExpanded}
                      onClick={() => {
                        setExpandedPlanTiers((previous) => {
                          const next = new Set(previous);
                          if (next.has(tier)) {
                            next.delete(tier);
                          } else {
                            next.add(tier);
                          }
                          return next;
                        });
                      }}
                    >
                      {isFeaturesExpanded ? (
                        <>
                          Show less
                          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                        </>
                      ) : (
                        <>
                          Show all ({allFeatures.length})
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                        </>
                      )}
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="mt-4 shrink-0 text-left sm:mt-0 sm:text-right"
                  onClick={() => handlePlanSelect(tier)}
                  aria-pressed={isSelected}
                >
                  {billingInterval === 'month' ? (
                    <>
                      <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                        {formatPlanPrice(plan, 'month')}
                        <span className="text-base font-semibold text-slate-500 dark:text-slate-400">
                          /mo
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        After 14-day free trial
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                        {formatRoundedMonthlyFromYearly(plan)}
                        <span className="text-base font-semibold text-slate-500 dark:text-slate-400">
                          /mo
                        </span>
                      </p>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                        Billed annually ({formatPlanPrice(plan, 'year')})
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        After 14-day free trial
                      </p>
                    </>
                  )}
                  <div className="mt-3 flex justify-end sm:justify-end">
                    <span
                      className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                        isSelected
                          ? 'border-primary-600 bg-primary-600 text-white dark:border-primary-400 dark:bg-primary-500'
                          : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900'
                      }`}
                      aria-hidden
                    >
                      {isSelected ? <Check className="h-3 w-3" /> : null}
                    </span>
                  </div>
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      <StarterLimitationsModal
        open={starterModalOpen}
        onOpenChange={setStarterModalOpen}
        limitations={limitations}
        onConfirm={() => {
          onSelectPlanTier?.('starter', { starterAcknowledged: true });
          setStarterModalOpen(false);
        }}
      />
    </div>
  );
}
